import React from 'react';
import classNames from 'classnames';

interface IProps {
  trigger: any;
  children: React.ReactNode;
  anchorSide?: 'left' | 'right';
  open?: boolean;
  loading?: boolean;
  onClose?: () => void;
}

export default function Popover({
  children,
  trigger,
  anchorSide = 'left',
  open = false,
  loading = false,
  onClose = () => {},
}: IProps) {

  const popoverRef = React.useRef<HTMLInputElement>();
  const [isOpen, toggleIsOpen] = React.useState(open);
  const [alignRight, setAlignRight] = React.useState(anchorSide === 'right');

  React.useEffect(() => {
    toggleIsOpen(open);
  }, [open]);

  useCloseEventHandler(isOpen, popoverRef, React.useCallback(() => { handleClose(); }, []));
  const classes = classNames(
    'popover',
    { 'popover--open': isOpen },
    { 'popover--anchor-right': alignRight },
    { 'popover--loading': loading },
  );

  if (!trigger) return null;

  function handleClick() {
    if (!isOpen) {
      const container = popoverRef.current.lastElementChild.getBoundingClientRect();
      const windowWidth = window?.innerWidth || 10000;
      if (container.right >= windowWidth) {
        setAlignRight(true);
      }
      if (container.left <= 0) {
        setAlignRight(false);
      }
      return toggleIsOpen(true);
    }
    return handleClose();
  }

  function handleClose() {
    toggleIsOpen(false);
    onClose();
  }

  return (
    <div
      className={classes}
      ref={popoverRef}
    >
      {React.cloneElement(
        trigger,
        { onClick: handleClick },
      )}
      <div className="popover__wrapper">
        <div className="popover__content">
          {children}
        </div>
      </div>
    </div>
  );
}

export function useCloseEventHandler(isOpen, ref, handler) {
  React.useEffect(() => {
    function listener(e: Event) {
      if (!ref.current || ref.current.contains(e.target)) {
        return;
      }
      handler(e);
    }
    function keyListener(e: KeyboardEvent) {
      const ESC = 27;
      if (e.keyCode === ESC && isOpen) {
        handler(e);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
      document.addEventListener('focusin', listener);
      document.addEventListener('keydown', keyListener);
    }

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
      document.removeEventListener('focusin', listener);
      document.removeEventListener('keydown', keyListener);
    };
  }, [isOpen, ref, handler]);
}
